import { useEffect } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigation, useNotification } from "@refinedev/core";
import { useDocumentTitle } from "@refinedev/react-router";
import { Plus, X } from "lucide-react";

import { BottomActionBar } from "@/components/bottom-action-bar";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useNotificationRulesRepository } from "@/pages/tina/notification-rules/hooks/use-notification-rules-repository";
import {
  type NotificationRulesCreateFormType,
  notificationRulesCreateSchema,
} from "@/pages/tina/notification-rules/schemas/notification-rule.form";

const RESOURCE = "tina-notification-rules";

export const NotificationRulesCreate = () => {
  const { createMany } = useNotificationRulesRepository();
  const { list } = useNavigation();
  const { open } = useNotification();
  const setTitle = useDocumentTitle();

  useEffect(() => {
    setTitle("Create Notification Rules");
  }, []);

  const form = useForm<NotificationRulesCreateFormType>({
    resolver: zodResolver(notificationRulesCreateSchema),
    defaultValues: {
      verdict: "",
      enabled: true,
      values: [{ value: "", explanation: "" }],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "values",
  });

  const onSubmit = async (submitted: NotificationRulesCreateFormType) => {
    const { created, failed } = await createMany(
      submitted.verdict,
      submitted.enabled,
      submitted.values,
    );
    if (failed.length === 0) {
      open?.({
        type: "success",
        message: "Notification rules created",
        description: `Created ${created.length} rule${
          created.length === 1 ? "" : "s"
        } for "${submitted.verdict}"`,
      });
    } else {
      const createdNote =
        created.length > 0 ? ` Created: ${created.join(", ")}.` : "";
      open?.({
        type: "error",
        message: "Some notification rules failed",
        description: `Failed: ${failed
          .map((entry) => entry.value)
          .join(", ")}.${createdNote}`,
      });
    }
    // Navigate regardless so any rows that did land are immediately visible.
    list(RESOURCE);
  };

  return (
    <div className="flex w-full max-w-6xl flex-col gap-4 p-8">
      <div>
        <h1 className="text-2xl font-semibold">Add notification rules</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter the verdict once, then add one or more (value, explanation)
          pairs. Each pair is saved as its own rule.
        </p>
      </div>

      <Form {...form}>
        {/* pb-24 keeps the last field clear of the fixed BottomActionBar. */}
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col gap-6 pb-24"
        >
          <div className="flex max-w-xl flex-col gap-6">
            <FormField
              control={form.control}
              name="verdict"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Verdict</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. locality" {...field} />
                  </FormControl>
                  <FormDescription>
                    The dimension these values belong to. All values below share
                    this verdict.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem className="flex items-center gap-3">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(checked) =>
                        field.onChange(checked === true)
                      }
                    />
                  </FormControl>
                  <FormLabel className="!mt-0">
                    Enabled (applies to every rule created here)
                  </FormLabel>
                </FormItem>
              )}
            />
          </div>

          <div className="flex flex-col gap-4">
            <span className="text-sm font-medium">Values</span>
            {fields.map((row, index) => (
              <div
                key={row.id}
                className="flex max-w-3xl items-start gap-3 rounded-lg border bg-card p-4"
              >
                <div className="flex flex-1 flex-col gap-4">
                  <FormField
                    control={form.control}
                    name={`values.${index}.value`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Value</FormLabel>
                        <FormControl>
                          <Input placeholder="e.g. local" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name={`values.${index}.explanation`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Explanation</FormLabel>
                        <FormControl>
                          <Textarea
                            placeholder="What this value means for notifications."
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Remove value"
                  disabled={fields.length === 1}
                  onClick={() => remove(index)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="self-start"
              onClick={() => append({ value: "", explanation: "" })}
            >
              <Plus className="mr-1 h-4 w-4" />
              Add value
            </Button>
          </div>

          <BottomActionBar className="justify-end">
            <Button type="submit" disabled={form.formState.isSubmitting}>
              Create rules
            </Button>
          </BottomActionBar>
        </form>
      </Form>
    </div>
  );
};
